import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { Solution } from '../../models/solution.model';
import { SolutionsService, SolutionFilter } from '../../services/solutions.service';

@Component({
    selector: 'app-solution-portfolio',
    imports: [TranslateModule],
    templateUrl: './solution-portfolio.component.html',
    styleUrl: './solution-portfolio.component.scss'
})
export class SolutionPortfolioComponent implements OnChanges {
  private readonly solutionsService = inject(SolutionsService);

  @Input() selectedCountry: string = '';
  @Input() set selectedIndustries(val: string[]) { this._selectedIndustries = val ?? []; }
  get selectedIndustries(): string[] { return this._selectedIndustries; }
  private _selectedIndustries: string[] = [];

  @Input() set selectedDisciplines(val: string[]) { this._selectedDisciplines = val ?? []; }
  get selectedDisciplines(): string[] { return this._selectedDisciplines; }
  private _selectedDisciplines: string[] = [];

  @Input() set selectedMaterials(val: string[]) { this._selectedMaterials = val ?? []; }
  get selectedMaterials(): string[] { return this._selectedMaterials; }
  private _selectedMaterials: string[] = [];

  /** Pre-selected solution names to restore on initial render (from persisted space data). */
  @Input() initialSelections: string[] = [];

  @Output() selectedSolutionNamesChange = new EventEmitter<string[]>();
  @Output() suggestedEditionIdsChange = new EventEmitter<string[]>();

  solutions: Solution[] = [];
  highlightedSolutions: Set<number> = new Set();
  selectedSolutions: Set<number> = new Set();
  private hasInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {
    const configChanged = changes['selectedCountry'] || changes['selectedIndustries'] ||
      changes['selectedDisciplines'] || changes['selectedMaterials'];
    if (!this.hasInitialized || configChanged) {
      this.loadSolutions();
    }
  }

  private loadSolutions(): void {
    const filter: SolutionFilter = {
      industries: this.selectedIndustries ?? [],
      disciplines: this.selectedDisciplines ?? [],
      materials: this.selectedMaterials ?? []
    };

    forkJoin({
      allSolutions: this.solutionsService.getSolutions(),
      matchingIds: this.solutionsService.getMatchingSolutionIds(filter)
    }).subscribe(({ allSolutions, matchingIds }) => {
      this.solutions = allSolutions;
      this.highlightedSolutions = new Set(matchingIds);
      if (!this.hasInitialized && this.initialSelections.length > 0) {
        // Restore persisted manual selections on first load
        const initialIds = new Set(
          allSolutions.filter(s => this.initialSelections.includes(s.name)).map(s => s.id)
        );
        this.selectedSolutions = initialIds.size > 0 ? initialIds : new Set(this.highlightedSolutions);
      } else {
        this.selectedSolutions = new Set(this.highlightedSolutions);
      }
      this.hasInitialized = true;
      this.emitSelectedSolutionNames();
      this.emitSuggestedEditionIds();
    });
  }

  private emitSelectedSolutionNames(): void {
    const names = this.solutions
      .filter(s => this.selectedSolutions.has(s.id))
      .map(s => s.name);
    this.selectedSolutionNamesChange.emit(names);
  }

  private emitSuggestedEditionIds(): void {
    const highlighted = this.solutions.filter(s => this.highlightedSolutions.has(s.id));
    const editionIds = this.solutionsService.getSuggestedEditionIds(highlighted);
    this.suggestedEditionIdsChange.emit(editionIds);
  }

  toggleSolution(solutionId: number): void {
    if (this.selectedSolutions.has(solutionId)) {
      this.selectedSolutions.delete(solutionId);
    } else {
      this.selectedSolutions.add(solutionId);
    }
    this.emitSelectedSolutionNames();
    this.emitSuggestedEditionIds();
  }

  isSolutionHighlighted(solutionId: number): boolean {
    return this.highlightedSolutions.has(solutionId);
  }

  isSolutionSelected(solutionId: number): boolean {
    return this.selectedSolutions.has(solutionId);
  }
}

